import { component } from '@smoovy/component';
import { EventEmitter, listenCompose, listenEl, Unlisten } from '@smoovy/event';
import { easings, Tween } from '@smoovy/tween';

import svcs from '../services';
import { CursorEvent } from './cursor';
import { CursorText } from './cursor-text';

export enum PlayerEvent {
  PLAY = 'play',
  PAUSE = 'pause',
  PAUSE_ALL = 'pauseall',
  DESTROY = 'destroy'
}

export interface IPlayer {
  pause(): void;
  play(): void;
}

@component({
  selector: '.player,[data-player]'
})
export class Player extends EventEmitter implements IPlayer {
  private audio: HTMLAudioElement;
  private unlisten?: Unlisten;
  private initialized = false;
  private service: EventEmitter;
  private playing = false;
  private cursor: EventEmitter;
  private cursorId: number;
  private cursorActive = false;
  private durationCheck = 0;
  private volumeState = { volume: 0 };

  constructor(
    protected element: HTMLElement
  ) {
    super();

    this.initService();

    this.audio = new Audio();
    this.cursorId = ++CursorText.id;
    this.unlisten = listenCompose(
      listenEl(this.element, 'click', () => this.togglePlay()),
      listenEl(this.audio, ['play', 'pause', ], () => this.updateState()),
      listenEl(this.element, 'mousemove', () => {
        if ( ! this.cursorActive && this.cursor) {
          this.cursorActive = true;

          this.cursor.emit(CursorEvent.UPDATE_TEXT, {
            id: this.cursorId,
            text: this.playing ? 'Pause' : 'Play'
          });
        }
      }),
      listenEl(this.element, 'mouseleave', () => {
        this.cursorActive = false;

        if (this.cursor) {
          this.cursor.emit(CursorEvent.UPDATE_TEXT, { id: this.cursorId });
        }
      })
    );
  }

  public getAudioNode(context: AudioContext) {
    return context.createMediaElementSource(this.audio);
  }

  private async initService() {
    this.service = await svcs.player.service;
    svcs.cursor.service.then((cursor) => this.cursor = cursor);
  }

  private initAudio() {
    this.audio.crossOrigin = 'anonymous';
    this.audio.src = this.element.dataset.src!;
    this.audio.volume = 0;
    this.initialized = true;
  }

  private updateState(paused = this.audio.paused) {
    if (paused) {
      this.service.emit(PlayerEvent.PAUSE, this);
      this.emit(PlayerEvent.PAUSE);
    } else {
      this.service.emit(PlayerEvent.PLAY, this);
      this.emit(PlayerEvent.PLAY);
    }

    this.element.classList.toggle(
      'is-playing',
      this.playing = !paused
    );

    clearInterval(this.durationCheck);

    if (this.playing) {
      this.durationCheck = setInterval(() => {
        if (this.audio.currentTime > this.audio.duration - 1) {
          this.pause();
        }
      }, 100) as any;
    }

    if (this.cursor) {
      this.cursor.emit(CursorEvent.REPLACE_TEXT, {
        id: this.cursorId,
        text: this.playing ? 'Pause' : 'Play'
      });
    }
  }

  public async pause() {
    if ( ! this.initialized) {
      this.initAudio();
    }

    this.updateState(true);
    this.fadeOut().then(() => this.audio.pause());
  }

  public async play() {
    if ( ! this.initialized) {
      this.initAudio();
    }

    this.audio.play();
    this.fadeIn();
  }

  public isPlaying() {
    return !this.audio.paused;
  }

  private async togglePlay() {
    if (this.audio.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  private setVolume(volume: number, duration = 0) {
    return new Promise<void>((resolve) => {
      Tween.fromTo(this.volumeState, { volume }, {
        easing: easings.Linear.none,
        duration,
        on: {
          update: ({ volume }) => this.audio.volume = parseFloat(volume.toFixed(3)),
          stop: () => resolve(),
          complete: () => resolve()
        }
      });
    });
  }

  private fadeOut(duration = 500) {
    return this.setVolume(0, duration);
  }

  private fadeIn(duration = 500) {
    return this.setVolume(0.15, duration);
  }

  public async onDestroy() {
    if ( ! this.audio.paused) {
      this.fadeOut().then(() => {
        this.pause();
        this.audio.src = '';
        this.service.emit(PlayerEvent.DESTROY);
      });
    } else  {
      this.audio.src = '';
      this.service.emit(PlayerEvent.DESTROY);
    }

    setTimeout(() => {
      if (this.unlisten) {
        this.unlisten();
        delete this.unlisten;
      }
    });

    clearInterval(this.durationCheck);
  }
}